import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, ArrowRight, CircleCheck, CircleX, ChevronDown, Mic, WandSparkles } from 'lucide-react';
import Testimonials from '../Testimonials';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

// Animation variants
const heroVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3
    }
  }
};

const sectionVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};

// Tonie stories
const stories = [
  {
    title: "Julian rettet den Drachen",
    image: "https://i.imgur.com/JFjHZH4.png",
    audioUrl: '/julian-rettet-den-drachen.m4a'
  },
  {
    title: "Emma im All",
    image: "https://i.imgur.com/kFqoh6s.png",
    audioUrl: '/emma-im-all.m4a'
  }
];

const FAQAccordion = ({ question, answer, isOpen, onClick }) => {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="border-b border-[#343434]/10"
    >
      <button
        className="w-full py-6 flex justify-between items-center text-left group"
        onClick={onClick}
      >
        <h3 className="font-freeman text-2xl md:text-[2rem] mb-2 text-[#343434] leading-[110%] tracking-[-0.03rem] md:tracking-[-0.05rem]">{question}</h3>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          <ChevronDown className="w-5 h-5 text-[#343434]" />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ 
              type: "spring",
              stiffness: 300,
              damping: 30
            }}
            className="overflow-hidden"
          >
            <div className="pb-8 text-[#343434] text-lg leading-relaxed max-w-[70ch] break-words font-aleo">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const TonieLanding = () => {
  const [activeAudioIndex, setActiveAudioIndex] = useState(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const audioRef = useRef(null);
  const [isHeroVisible, setIsHeroVisible] = useState(true);
  const heroRef = useRef(null);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  // Add scroll visibility logic
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsHeroVisible(entry.isIntersecting);
      },
      {
        root: null,
        rootMargin: '-80px',
        threshold: 0
      }
    );

    if (heroRef.current) {
      observer.observe(heroRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Add handleAudioPlay function
  const handleAudioPlay = (index) => {
    if (!audioRef.current) return;
    
    if (activeAudioIndex === index && isAudioPlaying) {
      audioRef.current.pause();
      setIsAudioPlaying(false);
    } else {
      if (activeAudioIndex !== index) {
        audioRef.current.src = stories[index].audioUrl;
        audioRef.current.currentTime = 0;
        setAudioProgress(0);
      }
      audioRef.current.play();
      setIsAudioPlaying(true);
      setActiveAudioIndex(index);
    }
  };

  return (
    <>
      <audio ref={audioRef} className="hidden">
        <source src="" type="audio/mp4" />
      </audio>

      {/* Navigation Bar */}
      <nav className="fixed top-0 z-50 w-full">
        <div 
          className="w-full"
          style={{
            backgroundImage: 'url(/nav-bg.svg)',
            backgroundSize: 'auto 120%',
            backgroundPosition: 'center',
            backgroundRepeat: 'repeat',
          }}
        >
          <div className="max-w-full sm:max-w-[90%] xl:max-w-[70rem] mx-auto px-4 sm:px-6 xl:px-6 relative">
            <div className="flex items-center justify-between h-16 lg:h-24">
              {/* Logo - always on the left for desktop */}
              <div className="hidden lg:block">
                <Link to="/" className="flex items-center">
                  <img 
                    src="/Logo.svg" 
                    alt="Plottie Logo" 
                    className="h-12 w-auto"
                  />
                </Link>
              </div>

              {/* Mobile logo with fade and slide animation */}
              <div className="lg:hidden">
                {/* Centered mobile logo */}
                <div 
                  className={`absolute left-1/2 top-1/2 transform -translate-x-1/2 ${
                    isHeroVisible 
                      ? 'opacity-100 -translate-y-1/2' 
                      : 'opacity-0 -translate-y-full'
                  }`}
                  style={{
                    transition: 'all 600ms cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  <Link to="/" className="flex items-center">
                    <img 
                      src="/Logo.svg" 
                      alt="Plottie Logo" 
                      className="h-8 w-auto"
                    />
                  </Link>
                </div>

                {/* Left-aligned mobile logo */}
                <div 
                  className={`absolute left-4 sm:left-6 top-1/2 transform ${
                    isHeroVisible 
                      ? 'opacity-0 -translate-y-full' 
                      : 'opacity-100 -translate-y-1/2'
                  }`}
                  style={{
                    transition: 'all 600ms cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  <Link to="/" className="flex items-center">
                    <img 
                      src="/Logo.svg" 
                      alt="Plottie Logo" 
                      className="h-8 w-auto"
                    />
                  </Link>
                </div>
              </div>
              
              {/* Button - always visible on desktop */}
              <div className="hidden lg:block">
                <a 
                  href="/erstellen"
                  className="px-5 py-3 bg-[#FFA23E] text-[#343434] rounded-full font-freeman text-lg md:tracking-[-0.04rem] hover:bg-[#ff9625] transition-all duration-300"
                >
                  Geschichte erstellen
                </a>
              </div>

              {/* Mobile button with fade animation */}
              <div 
                className={`lg:hidden ${
                  isHeroVisible ? 'opacity-0' : 'opacity-100'
                }`}
                style={{
                  transition: 'all 600ms ease-out'
                }}
              >
                <a 
                  href="/erstellen"
                  className="px-3 py-2 bg-[#FFA23E] text-[#343434] rounded-full font-freeman text-base hover:bg-[#ff9625] transition-all duration-300 tracking-[-0.03rem]"
                >
                  Geschichte erstellen
                </a>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <motion.div 
        ref={heroRef}
        className="sm:mx-0 pt-4"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        <div className="max-w-full sm:max-w-[90%] xl:max-w-[70rem] mx-auto px-4 sm:px-6 py-16 md:pt-56 md:pb-32 flex flex-col items-center md:h-auto md:justify-start">
          <div className="w-full text-center">
            <div className="max-w-[90%] md:max-w-[80%] mx-auto">
              <motion.h1 
                variants={heroVariants}
                className="text-[#343434] text-[2.8rem] sm:text-[3.25rem] md:text-[4.5rem] leading-[110%] tracking-[-0.105rem] md:tracking-[-0.135rem] font-freeman mb-3 md:mb-6"
              >
                Kreativ-Tonies clever nutzen - zauberhafte Hörgeschichten ohne selbst vorzulesen
              </motion.h1>
              <motion.p 
                variants={heroVariants}
                className="text-[#343434] text-base md:text-[1.25rem] font-normal leading-[150%] font-aleo"
              >
                Mit Plottie verwandelst du einen leeren Kreativ-Tonie ganz einfach in einen personalisierten Geschichtenerzähler - ohne selbst die perfekte Geschichte erfinden oder vorlesen zu müssen.
              </motion.p>
              
              <motion.div 
                variants={heroVariants}
                className="relative z-10 mt-8 md:mt-12"
              >
                <div className="max-w-2xl mx-auto text-center flex flex-col items-center">
                  <a 
                    href="/erstellen"
                    className="h-14 bg-[#FFA23E] text-[#343434] px-8 py-2 rounded-full font-freeman text-[1.25rem] font-normal leading-[110%] tracking-[-0.0375rem] inline-flex items-center justify-center hover:bg-[#FF9925] transition-colors duration-300"
                  >
                    Jetzt Geschichte erstellen
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Main Content Section */}
      <motion.section
        className="w-full bg-[#FFF3DD] py-8 md:py-[16rem] relative"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        {/* Add decorative image */}
        <div className="w-full absolute -top-[1px] select-none pointer-events-none" aria-hidden="true">
          <img
            src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/footer-deco-qeD3pts3cCvDUvK9Y5X2MMgg2w172y.png"
            alt=""
            className="w-full"
          />
        </div>

        <div className="max-w-full sm:max-w-[90%] xl:max-w-[70rem] mx-auto px-4 sm:px-6 py-16 md:py-32">
          <h2 className="text-[#7785FC] text-[1.5rem] font-bold leading-normal font-['Edu_TAS_Beginner'] text-center mb-4">
            KREATIV-TONIE BESPIELEN
          </h2>
          <h3 className="text-[#343434] text-[2.5rem] md:text-[4rem] leading-[110%] tracking-[-0.075rem] md:tracking-[-0.12rem] text-center font-freeman mb-12">
            Ein leerer Kreativ-Tonie ist ein unbeschriebenes Buch…
          </h3>
          
          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            <div className="bg-[#EDD1BA] rounded-3xl p-8">
              <h4 className="text-[#343434] text-[1.5rem] md:text-[2rem] leading-[110%] tracking-[-0.045rem] font-freeman mb-4">
                Kennst du das?
              </h4>
              <p className="text-[#343434] text-base md:text-lg font-aleo leading-relaxed">
                Du hast einen leeren Kreativ-Tonie, aber die Ideen dafür fehlen. Mit Plottie bekommst du eine professionell eingesprochene Geschichte, die genau zu deinem Kind passt. Kein stundenlanges Suchen mehr nach einer Vorlesegeschichte, die dann doch nur so halb passend ist.
              </p>
              <a 
                href="/#warum-plottie"
                className="text-[#343434] hover:text-[#6674eb] transition-colors mt-4 inline-flex items-center gap-2 font-aleo font-bold group"
              >
                Mehr erfahren
                <ArrowRight 
                  size={20}
                  className="transform transition-transform duration-300 group-hover:translate-x-1"
                />
              </a>
            </div>
            
            <div className="bg-[#EDD1BA] rounded-3xl p-8">
              <h4 className="text-[#343434] text-[1.5rem] md:text-[2rem] leading-[110%] tracking-[-0.045rem] font-freeman mb-4">
                Einfach besonders.
              </h4>
              <p className="text-[#343434] text-base md:text-lg font-aleo leading-relaxed">
                Unsere Geschichten sind so individuell wie dein Kind. Dafür musst du aber nicht selbst kreativ werden oder dich mit der Aufnahme herumschlagen. Beantworte einfach ein paar Fragen zu deinem Kind, und wir erstellen eine maßgeschneiderte Geschichte mit professioneller Sprecherstimme.
              </p>
              <a 
                href="/#warum-plottie"
                className="text-[#343434] hover:text-[#6674eb] transition-colors mt-4 inline-flex items-center gap-2 font-aleo font-bold group"
              >
                Mehr erfahren
                <ArrowRight 
                  size={20}
                  className="transform transition-transform duration-300 group-hover:translate-x-1"
                />
              </a>
            </div>
          </div>
        </div>

        {/* Story Preview Section */}
        <div className="max-w-full sm:max-w-[95%] xl:max-w-[80%] 4xl:max-w-[90rem] mx-auto px-4 sm:px-6 mt-24">
          <div className="flex flex-col lg:flex-row lg:items-center md:gap-16 lg:gap-24 py-0 lg:py-40">
            {/* Story cards */}
            <div className="flex-1 mb-12 lg:mb-0 order-1 lg:order-2">
              <div className="grid grid-cols-2 gap-6">
                {stories.map((story, index) => (
                  <div 
                    key={index}
                    className="relative w-full aspect-[4/5] rounded-2xl md:rounded-3xl lg:rounded-[32px] overflow-hidden group"
                  >
                    <img 
                      src={story.image} 
                      alt={story.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    {/* Audio overlay */}
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
                      <button 
                        onClick={() => handleAudioPlay(index)}
                        className="group flex items-center justify-center gap-1 rounded-full bg-[#EDD1BA]/80 px-4 py-2 text-[#343434] text-[0.875rem] font-bold leading-[150%] relative overflow-hidden hover:scale-110 transition-all duration-300"
                        style={{
                          fontFamily: 'Aleo',
                          fontWeight: 700,
                          lineHeight: '1.5rem',
                          fontStyle: 'normal',
                        }}
                      >
                        {/* Progress bar overlay */}
                        {activeAudioIndex === index && (
                          <div 
                            className="absolute left-0 top-0 h-full bg-[#7785FC] transition-all duration-100 ease-linear z-0 rounded-full"
                            style={{ width: `${audioProgress}%` }}
                          />
                        )}
                        
                        {/* Button content */}
                        <div className="relative z-10 flex items-center justify-center gap-1">
                          <div className="transition-opacity duration-300">
                            {activeAudioIndex === index && isAudioPlaying ? (
                              <Pause size={14} className="relative bottom-[1px] fill-current" />
                            ) : (
                              <Play size={14} className="relative bottom-[1px] fill-current" />
                            )}
                          </div>
                          <span className="text-[#343434]">
                            Hörprobe
                          </span>
                        </div>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Text content */}
            <div className="flex-1 order-2 lg:order-1">
              <h2 className="text-[#7785FC] text-[1.5rem] font-bold leading-normal font-['Edu_TAS_Beginner'] md:mb-4">
                LOS GEHT'S
              </h2>
              <h2 className="text-[#343434] text-[2.5rem] md:text-[4rem] leading-[110%] tracking-[-0.075rem] md:tracking-[-0.12rem] font-freeman mb-6 md:mb-12">
                So einfach wird der Kreativ-Tonie zum Liebling deines Kindes
              </h2>
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#7785FC] flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-freeman">1</span>
                  </div>
                  <p className="text-[#343434] text-lg md:text-xl font-aleo leading-relaxed">
                    Beantworte ein paar kurze Fragen
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#7785FC] flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-freeman">2</span>
                  </div>
                  <p className="text-[#343434] text-lg md:text-xl font-aleo leading-relaxed">
                    Personalisiere die Geschichte mit Details deines Kindes
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#7785FC] flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-freeman">3</span>
                  </div>
                  <p className="text-[#343434] text-lg md:text-xl font-aleo leading-relaxed">
                    Lade die fertige MP3 auf deinen Kreativ-Tonie
                  </p>
                </div>
                <a 
                  href="/erstellen"
                  className="h-14 bg-[#FFA23E] text-[#343434] px-6 rounded-full font-freeman text-[1.25rem] font-normal leading-[110%] tracking-[-0.0375rem] inline-flex items-center justify-center hover:bg-[#FF9925] transition-colors duration-300 mt-8"
                >
                  Jetzt Hörgeschichte erstellen
                </a>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Comparison Section */}
      <motion.section
        className="w-full bg-[#FFA23E] py-24 md:pb-[32rem] md:pt-[12rem] relative"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <div className="max-w-full sm:max-w-[90%] xl:max-w-[70rem] mx-auto px-4 sm:px-6">
          <h2 className="text-[#343434] text-[2.5rem] md:text-[4rem] leading-[110%] tracking-[-0.075rem] md:tracking-[-0.12rem] text-center font-freeman mb-6 md:mb-16">
            Warum Plottie Hörgeschichten statt selbst aufnehmen?
          </h2>
          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            <div className="bg-[#FFF3DD] rounded-3xl p-8">
              <h4 className="text-[#343434] text-[1.5rem] md:text-[2rem] leading-[110%] tracking-[-0.045rem] font-freeman mb-6 flex items-center gap-2">
              <Mic size={24} />Selbst aufnehmen
              </h4>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <CircleX className="text-red-500 w-6 h-6 min-w-[24px] min-h-[24px]" />
                  </div>
                  <span className="text-[#343434] text-lg font-aleo">Eine passende Geschichte zu finden kostet viel Zeit</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <CircleX className="text-red-500 w-6 h-6 min-w-[24px] min-h-[24px]" />
                  </div>
                  <span className="text-[#343434] text-lg font-aleo">Vorgefertigte Geschichten wirken meist unpersönlich</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <CircleX className="text-red-500 w-6 h-6 min-w-[24px] min-h-[24px]" />
                  </div>
                  <span className="text-[#343434] text-lg font-aleo">Die kostbare Freizeit lässt sich sinnvoller nutzen</span>
                </div>
              </div>
            </div>
            <div className="bg-[#FFF3DD] rounded-3xl p-8">
              <h4 className="text-[#343434] text-[1.5rem] md:text-[2rem] leading-[110%] tracking-[-0.045rem] font-freeman mb-6 flex items-center gap-2">
                <WandSparkles size={24} />Mit Plottie erstellen
              </h4>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <CircleCheck className="text-[#7785FC] w-6 h-6 min-w-[24px] min-h-[24px]" />
                  </div>
                  <span className="text-[#343434] text-lg font-aleo">Genau passend zu deinem Kind, kein Einheitsbrei</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <CircleCheck className="text-[#7785FC] w-6 h-6 min-w-[24px] min-h-[24px]" />
                  </div>
                  <span className="text-[#343434] text-lg font-aleo">Sprecherstimmen, die Geschichten zum Leben erwecken</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <CircleCheck className="text-[#7785FC] w-6 h-6 min-w-[24px] min-h-[24px]" />
                  </div>
                  <span className="text-[#343434] text-lg font-aleo">In wenigen Minuten fertig – einfacher geht es nicht</span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-center mt-12">
            <a 
              href="/erstellen"
              className="h-14 bg-[#FFF3DD] text-[#343434] px-8 py-2 rounded-full font-freeman text-[1.25rem] font-normal leading-[110%] tracking-[-0.0375rem] inline-flex items-center justify-center hover:bg-[#7785FC] transition-all duration-300"
            >
              Jetzt Hörgeschichte erstellen
            </a>
          </div>
        </div>

        {/* Bottom decorative image */}
        <div className="w-full absolute -bottom-[1px] scale-x-[-1] select-none pointer-events-none" aria-hidden="true">
          <img
            src="https://i.imgur.com/DHRxkYk.png"
            alt=""
            className="w-full"
          />
        </div>
      </motion.section>

      {/* Testimonials Section */}
      <motion.section
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="w-full py-12 md:py-56"
      >
        <h2 className="text-[#343434] text-[2.5rem] md:text-[4rem] leading-[110%] tracking-[-0.075rem] md:tracking-[-0.12rem] text-center font-freeman pb-12 md:pb-4">
          Das sagen Eltern über Plottie.
        </h2>
        <Testimonials />
      </motion.section>

      {/* Image Divider */}
      <motion.div
        initial={{ opacity: 0, scale: 1.1 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ 
          duration: 1.2,
          ease: [0.25, 0.1, 0, 1]
        }}
        className="w-full mb-8 md:my-20"
        style={{ transformOrigin: 'top' }}
      >
        <div className="relative w-full md:h-auto">
          {/* Mobile square image (aspect-ratio: 1/1) */}
          <div className="block md:hidden relative w-full pb-[100%] overflow-hidden">
            <img
              src="https://i.imgur.com/XUPdG0k.jpeg"
              alt="Kind hört Tonie-Box"
              className="absolute w-full h-full object-cover object-center pointer-events-none select-none"
              loading="lazy"
            />
          </div>
          
          {/* Desktop original image */}
          <div className="hidden md:block relative w-full h-[1200px] overflow-hidden md:pb-40">
            <img
              src="https://i.imgur.com/XUPdG0k.jpeg"
              alt="Kind hört Tonie-Box"
              className="w-full h-full object-cover object-center pointer-events-none select-none"
              loading="lazy"
            />
          </div>
        </div>
      </motion.div>

      {/* FAQ Section */}
      <motion.section
        className="w-full py-24 md:py-32 relative"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <div className="max-w-full sm:max-w-[90%] xl:max-w-[70rem] mx-auto px-4 sm:px-6">
          <div className="flex flex-col lg:flex-row lg:gap-16 lg:items-start">
            <div className="mb-16 lg:mb-0 lg:w-[40%] lg:sticky lg:top-32">
              <h2 className="text-[#7785FC] text-[1.125rem] md:text-[1.5rem] font-bold leading-normal font-['Edu_TAS_Beginner'] mb-2">
                DEINE FRAGEN
              </h2>
              <h3 className="text-[#343434] text-[2.125rem] md:text-[4.5rem] leading-[110%] tracking-[-0.06375rem] md:tracking-[-0.135rem] font-freeman">
                Alles zum Thema Kreativ-Tonie
              </h3>
            </div>
            
            <div className="lg:w-[60%]">
              <div className="space-y-4">
                {[
                  {
                    question: "Wie übertrage ich die Geschichte auf meinen Kreativ-Tonie?",
                    answer: "Nach dem Kauf erhältst du eine MP3-Datei, die du ganz einfach über die Tonie-Cloud auf deinen Kreativ-Tonie übertragen kannst. Der Upload funktioniert genau wie bei selbst aufgenommenen Geschichten."
                  },
                  {
                    question: "Wie viele Minuten passen auf einen Kreativ-Tonie?",
                    answer: "Ein Kreativ-Tonie bietet 90 Minuten Speicherplatz. Unsere Geschichten sind etwa 5 Minuten lang, sodass noch viel Platz für weitere Geschichten bleibt."
                  },
                  {
                    question: "Funktioniert das mit allen Kreativ-Tonies?",
                    answer: "Ja, unsere Geschichten sind mit allen Kreativ-Tonies kompatibel - egal ob neu oder bereits bespielt."
                  }
                ].map((faq, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  >
                    <FAQAccordion
                      question={faq.question}
                      answer={faq.answer}
                      isOpen={openFaqIndex === `tonie-${index}`}
                      onClick={() => setOpenFaqIndex(openFaqIndex === `tonie-${index}` ? null : `tonie-${index}`)}
                    />
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Final CTA Section */}
      <motion.section
        className="w-full md:w-[60%] mx-auto bg-[#EDD1BA] py-24 pb-36 relative"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        {/* Background Image */}
        <div 
          className="absolute inset-0 w-full h-full"
          style={{
            backgroundImage: 'url(https://i.imgur.com/ZNj20Aq.png)',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'contain',
            transformOrigin: 'center',
          }}
        />
        
        {/* Content */}
        <div className="max-w-full sm:max-w-[90%] xl:max-w-[70rem] mx-auto px-4 sm:px-6 text-center relative z-10 py-12 md:py-40">
          <h2 className="text-[#343434] text-[2.5rem] md:text-[4rem] leading-[110%] tracking-[-0.075rem] md:tracking-[-0.12rem] font-freeman mb-6">
            In wenigen Minuten zur perfekten Geschichte für deinen Kreativ-Tonie
          </h2>
          <a 
            href="/erstellen"
            className="h-14 bg-[#FFF3DD] text-[#343434] px-8 py-2 rounded-full font-freeman text-[1.25rem] font-normal leading-[110%] tracking-[-0.0375rem] inline-flex items-center justify-center hover:bg-[#7785FC] transition-all duration-300"
          >
            Jetzt Tonie-Geschichte erstellen
          </a>
        </div>
      </motion.section>

      {/* SEO Meta Tags */}
      <Helmet>
        <title>Kreativ-Tonie Geschichten erstellen | Hörgeschichten für die Toniebox</title>
        <meta name="description" content="Erstelle individuelle Hörgeschichten für deinen Kreativ-Tonie. Kinderleichte Bedienung, sofort auf die Toniebox übertragbar. Keine Aufnahme nötig ✓ Großartige Tonqualität ✓" />
        <meta name="keywords" content="Kreativ-Tonie, Toniebox, personalisierte Geschichten, Hörspiele, Kindergeschichten, Tonie Geschichten, Kreativ-Tonie befüllen, Toniebox Geschichten, Kreativ-Tonie Inhalt, Toniebox Hörspiele, personalisierte Tonie Geschichten, Tonie Cloud Upload, Kreativ-Tonie MP3" />
      </Helmet>
    </>
  );
};

export default TonieLanding; 